import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { generateDummyJobs } from "@/utils/dummy-data/jobs";
import { SortField, SortOrder } from "@/components/candidates/CandidatesViewOptions";

export interface JobData {
	id: string;
	title: string;
	company: {
		id: string;
		name: string;
	};
	skills: string[];
	createdAt: string;
	description?: string;
	aiSummary?: string;
	matchCount?: number;
	contactId?: string;

	// Fields
	employmentType?: string;
	timeCommitment?: string;
	contractDuration?: string;
	startDate?: string;
	endDate?: string;
	seniorityLevel?: string;
	budget?: string;
	budgetFreelance?: string;
	locationType?: string;
	workplaceBreakdown?: string;
	hardSkills?: string[];
	softSkills?: string[];
	educationLevel?: string;
	yearsOfExperience?: string;
	languageRequirements?: string;
	extraLang?: string;
	cultureTags?: string[];
	applicationDeadline?: string;
	jobPostingDate?: string;
	source?: string;
	assignedRecruiter?: string;
	employmentClassification?: string;
}

export const useJobsData = () => {
	const [jobs, setJobs] = useState<JobData[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");
	const [sortField, setSortField] = useState<SortField>("dateCreated");
	const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

	const fetchJobs = useCallback(async () => {
		try {
			setIsLoading(true);

			let dbSortField = "created_at"; // default
			if (sortField === "name") {
				dbSortField = "title";
			} else if (sortField === "matchScore") {
				dbSortField = "match_count";
			} else if (sortField === "dateCreated") {
				dbSortField = "created_at";
			}

			// First check if the new columns exist
			const { error: columnCheckError } = await supabase
				.from('jobs')
				.select('end_date, budget_freelance, extra_lang, time_commitment, ai_summary')
				.limit(1);

			// Construct the query based on whether the new columns exist
			let query;

			if (!columnCheckError) {
				// New columns exist, include them in the query
				query = supabase
					.from('jobs')
					.select(`
            id,
            title,
            description,
            skills,
            created_at,
            contact_id,
            company_id,
            companies(id, name),
            employment_type,
            time_commitment,
            contract_duration,
            start_date,
            end_date,
            seniority_level,
            budget,
            budget_freelance,
            location_type,
            workplace_breakdown,
            hard_skills,
            soft_skills,
            education_level,
            years_of_experience,
            language_requirements,
            extra_lang,
            culture_tags,
            application_deadline,
            job_posting_date,
            source,
            assigned_recruiter,
            employment_classification,
            ai_summary
          `)
					.order(dbSortField, { ascending: sortOrder === "asc" });
			} else {
				// Fall back to the original columns
				query = supabase
					.from('jobs')
					.select(`
            id,
            title,
            description,
            skills,
            created_at,
            contact_id,
            company_id,
            companies(id, name),
            employment_type,
            contract_duration,
            start_date,
            seniority_level,
            budget,
            location_type,
            workplace_breakdown,
            hard_skills,
            soft_skills,
            education_level,
            years_of_experience,
            language_requirements,
            culture_tags,
            application_deadline,
            job_posting_date,
            source,
            assigned_recruiter,
            employment_classification
          `)
					.order(dbSortField, { ascending: sortOrder === "asc" });
			}

			const { data, error } = await query;

			if (error) {
				throw error;
			}

			if (!data) {
				setJobs([]);
				return;
			}

			const formattedJobs: JobData[] = data.map(job => ({
				id: job.id,
				title: job.title,
				company: {
					id: job.company_id,
					name: job.companies?.name || 'Unknown Company'
				},
				skills: job.skills || [],
				createdAt: job.created_at,
				description: job.description,
				aiSummary: job.ai_summary,
				contactId: job.contact_id,

				employmentType: job.employment_type,
				timeCommitment: job.time_commitment,
				contractDuration: job.contract_duration,
				startDate: job.start_date,
				endDate: job.end_date,
				seniorityLevel: job.seniority_level,
				budget: job.budget,
				budgetFreelance: job.budget_freelance,
				locationType: job.location_type,
				workplaceBreakdown: job.workplace_breakdown,
				hardSkills: job.hard_skills,
				softSkills: job.soft_skills,
				educationLevel: job.education_level,
				yearsOfExperience: job.years_of_experience,
				languageRequirements: job.language_requirements,
				extraLang: job.extra_lang,
				cultureTags: job.culture_tags,
				applicationDeadline: job.application_deadline,
				jobPostingDate: job.job_posting_date,
				source: job.source,
				assignedRecruiter: job.assigned_recruiter,
				employmentClassification: job.employment_classification
			}));

			setJobs(formattedJobs);
		} catch (error) {
			console.error("Error fetching jobs:", error);
			toast({
				title: "Error",
				description: "Failed to load job offers",
				variant: "destructive",
			});
		} finally {
			setIsLoading(false);
		}
	}, [sortField, sortOrder]);

	useEffect(() => {
		fetchJobs();
	}, [fetchJobs]);

	const filteredJobs = jobs.filter(job => {
		if (!searchTerm) return true;

		const search = searchTerm.toLowerCase();
		return (
			job.title.toLowerCase().includes(search) ||
			job.company.name.toLowerCase().includes(search) ||
			(job.description && job.description.toLowerCase().includes(search)) ||
			job.skills.some(skill => skill.toLowerCase().includes(search)) ||
			(job.hardSkills && job.hardSkills.some(skill => skill.toLowerCase().includes(search))) ||
			(job.softSkills && job.softSkills.some(skill => skill.toLowerCase().includes(search))) ||
			(job.employmentType && job.employmentType.toLowerCase().includes(search)) ||
			(job.timeCommitment && job.timeCommitment.toLowerCase().includes(search)) ||
			(job.seniorityLevel && job.seniorityLevel.toLowerCase().includes(search))
		);
	});

	const addJob = async (jobData: any): Promise<string | undefined> => {
		console.log('Adding job with data:', jobData);

		try {
			const { data: sessionData } = await supabase.auth.getSession();
			const userUuid = sessionData.session?.user?.id;

			let organizationUuid = null;

			if (userUuid) {
				const { data: userOrg } = await supabase
					.from('user_organization')
					.select('organization_uuid')
					.eq('user_uuid', userUuid)
					.single();

				organizationUuid = userOrg?.organization_uuid;
			}

			const skillsArray = jobData.skills
				? Array.isArray(jobData.skills) ? jobData.skills
					: typeof jobData.skills === 'string' ? jobData.skills.split(',').map((skill: string) => skill.trim())
						: []
				: [];

			console.log('Inserting job with these skills:', skillsArray);
			console.log('AI Summary value:', jobData.ai_summary);

			const insertData = {
				title: jobData.title,
				company_id: jobData.companyId,
				description: jobData.description,
				skills: skillsArray.length > 0 ? skillsArray : null,
				contact_id: jobData.contactId || null,
				organization_uuid: organizationUuid,

				// Additional fields with new field mappings
				employment_type: jobData.employment_type,
				time_commitment: jobData.time_commitment,
				contract_duration: jobData.contract_duration,
				start_date: jobData.start_date,
				end_date: jobData.end_date,
				seniority_level: jobData.seniority_level,
				budget: jobData.budget,
				budget_freelance: jobData.budget_freelance,
				location_type: jobData.location_type,
				workplace_breakdown: jobData.workplace_breakdown,
				hard_skills: jobData.hard_skills,
				soft_skills: jobData.soft_skills,
				education_level: jobData.education_level,
				years_of_experience: jobData.years_of_experience,
				language_requirements: jobData.language_requirements,
				extra_lang: jobData.extra_lang,
				culture_tags: jobData.culture_tags,
				application_deadline: jobData.application_deadline,
				job_posting_date: jobData.job_posting_date,
				source: jobData.source,
				assigned_recruiter: jobData.assigned_recruiter,
				employment_classification: jobData.employment_classification,
				ai_summary: jobData.ai_summary
			};

			console.log('Final insert data:', insertData);

			const { data, error } = await supabase
				.from('jobs')
				.insert(insertData)
				.select()
				.single();

			if (error) {
				console.error("Supabase error when adding job:", error);
				throw error;
			}

			await triggerAIAnalysisForJob(data.id);
			document.location.href = `/jobs/${data.id}`; // Redirect to the job detail page

			// Call fetchJobs to refresh the jobs list
			fetchJobs();

			toast({
				title: "Success",
				description: "Job offer has been created successfully",
			});

			return data.id;
		} catch (error) {
			console.error("Error adding job:", error);
			toast({
				title: "Error",
				description: "Failed to create job offer: " + (error instanceof Error ? error.message : "Unknown error"),
				variant: "destructive",
			});
			return undefined;
		}
	};

	const generateJobs = async () => {
		try {
			await generateDummyJobs();
			fetchJobs();
			toast({
				title: "Success",
				description: "Dummy jobs have been generated successfully",
			});
			return true;
		} catch (error) {
			console.error("Error generating jobs:", error);
			toast({
				title: "Error",
				description: "Failed to generate dummy jobs",
				variant: "destructive",
			});
			return false;
		}
	};

	return {
		jobs: filteredJobs,
		isLoading,
		searchTerm,
		setSearchTerm,
		sortField,
		setSortField,
		sortOrder,
		setSortOrder,
		addJob,
		generateJobs,
		fetchJobs
	};
};

export const triggerAIAnalysisForJob = async (jobId: string) => {
	console.log('Triggering AI analysis for job ID:', jobId);

	console.log("Calling analyse-job-offer function with:", {
		jobId
	});

	const aiResponse = await supabase.functions.invoke('analyse-job-offer', {
		body: {
			jobOfferId: jobId
		}
	});

	if (aiResponse.error) {
		console.error("AI analysis error:", aiResponse.error);
		throw new Error(`AI Analysis Failed: ${aiResponse.error.message || 'Unknown error'}`);
	}

	console.log("AI analysis completed successfully:", aiResponse.data);
	return aiResponse.data;
}
